const {registerBlockType} = wp.blocks;
const {useState, useEffect} = wp.element;
const {Spinner} = wp.components;
const {__} = wp.i18n;

const POSTS_PER_PAGE = 8;

const Pagination = ({currentPage, totalPages, onPageChange}) => {
  const range = (start, end) => {
    let length = end - start + 1;
    return Array.from({length}, (_, idx) => idx + start);
  };

  const fetchPageNumbers = () => {
    const totalNumbers = 5;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      let pages = range(startPage, endPage);

      const hasLeftSpill = startPage > 2;
      const hasRightSpill = (totalPages - endPage) > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      if (hasLeftSpill && !hasRightSpill) {
        const extraPages = range(startPage - spillOffset, startPage - 1);
        pages = ['DOTS', ...extraPages, ...pages];
      } else if (!hasLeftSpill && hasRightSpill) {
        const extraPages = range(endPage + 1, endPage + spillOffset);
        pages = [...pages, ...extraPages, 'DOTS'];
      } else if (hasLeftSpill && hasRightSpill) {
        pages = ['DOTS', ...pages, 'DOTS'];
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  if (totalPages === 1) {
    return null;
  }

  return (
    <div className="pagination">
      {currentPage > 1 && (
        <a href="#" onClick={() => onPageChange(currentPage - 1)} className="pagination-prev">Prev</a>
      )}
      {fetchPageNumbers().map((page, index) =>
        page === 'DOTS' ? (
          <i key={index} className="pagination-dots">...</i>
        ) : (
          <a
            key={index}
            href="#"
            className={`pagination-page-number ${currentPage === page ? 'current' : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </a>
        )
      )}
      {currentPage < totalPages && (
        <a href="#" onClick={() => onPageChange(currentPage + 1)} className="pagination-next">Next</a>
      )}
    </div>
  );
};

registerBlockType('mailmetrics/insights', {
  title: 'Insights',
  icon: 'category',
  category: 'layout',

  edit: ({attributes, setAttributes}) => {
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [postsByCategory, setPostsByCategory] = useState({});
    const [allPosts, setAllPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
      wp.apiFetch({path: '/wp/v2/categories'}).then(categories => {
        const categoriesWithPostsPromises = categories.map(category =>
            wp.apiFetch({path: `/wp/v2/posts?categories=${category.id}&per_page=1`})
                .then(posts => posts.length > 0 ? category : null)
        );
        Promise.all(categoriesWithPostsPromises).then(results => {
          const filteredCategories = results.filter(category => category != null);
          setCategories(filteredCategories);
        });
      });
    }, []);

    useEffect(() => {
      if (activeTab !== null) {
        fetchPosts(activeTab, currentPage);
      }
    }, [activeTab, currentPage]);

    const fetchPosts = (categoryId, page) => {
      setIsLoading(true);

      const postsPerPage = page === 1 ? POSTS_PER_PAGE : (POSTS_PER_PAGE + 1);
      const categoryQueryParam = categoryId !== 'all' ? `&categories=${categoryId}` : '';

      const url = new URL(wpApiSettings.root + 'wp/v2/posts');
      url.searchParams.append('_embed', '');
      url.searchParams.append('page', page);
      url.searchParams.append('per_page', postsPerPage);
      url.search += categoryQueryParam;

      fetch(url, {
        headers: {
          'X-WP-Nonce': wpApiSettings.nonce,
        },
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          setTotalPages(parseInt(response.headers.get('X-WP-TotalPages')));
          return response.json();
        })
        .then(posts => {
          return Promise.all(posts.map(post => {
            // Fetch category name for each post
            return wp.apiFetch({ path: `/wp/v2/categories/${post.categories[0]}` })
              .then(category => {
                post.categoryName = category.name; // Add category name to the post object
                return post;
              });
          }));
        })
        .then(postsWithCategories => {
          if (categoryId === 'all') {
            setAllPosts(postsWithCategories);
          } else {
            setPostsByCategory(prev => ({...prev, [categoryId]: postsWithCategories}));
          }
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error:', error);
          setIsLoading(false);
        });
    };

    const handleTabClick = (categoryId) => {
      setActiveTab(categoryId);
      setCurrentPage(1);
    };

    const handlePaginationClick = (page) => {
      setCurrentPage(page);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('default', {month: 'long', year: 'numeric'});
    };

    const renderPost = (post, index) => {
      const isExcerptVisible = currentPage === 1 && index === 0;
      const categoryName = post.categoryName || '';

      return (
        <div key={post.id} className={`article-item ${isExcerptVisible ? 'article-item-first' : ''}`}>
          <p className="article-item-date">
            {categoryName}<span className="dividier"></span>{formatDate(post.date)}
          </p>
          <h4 dangerouslySetInnerHTML={{__html: post.title.rendered}}></h4>
          {isExcerptVisible && (
            <p dangerouslySetInnerHTML={{__html: post.excerpt.rendered}} className="article-item-excerpt"/>
          )}
          {post._embedded['wp:featuredmedia'] && (
            <div className="article-item-bottom">
              <a href={post.link}>Read more</a>
              <img src={post._embedded['wp:featuredmedia'][0].source_url} alt={post._embedded['wp:featuredmedia'][0].alt_text}/>
            </div>
          )}
        </div>
      );
    };

    return (
        <section className="tabs-section">
          <div className="inner">
            <div>
              <div data-type="insights" className="tabs-container tabs-container-with-dropdownmenu">
                <div className="tabs-tab-list">
                  <div
                      className={`tabs-tab-list-item ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => handleTabClick('all')}
                  >
                    <p>All</p>
                  </div>
                  {categories.map(category => (
                      <div key={category.id}
                           data-tab={`tab-${category.id}`}
                           className={`tabs-tab-list-item ${activeTab === category.id ? 'active' : ''}`}
                           onClick={() => handleTabClick(category.id)}
                      >
                        <p>{category.name}</p>
                      </div>
                  ))}
                </div>
                <div className="tabs-tab-content">
                  <div
                      className={`tabs-tab-content-item ${activeTab === 'all' ? 'active' : ''}`}
                      data-content="tab-all"
                  >
                    <div className="article-list-wrapper">
                      {isLoading && <Spinner />}
                      {allPosts.map((post, index) => renderPost(post, index))}
                    </div>
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePaginationClick}
                        />
                    )}
                  </div>
                  {categories.map(category => (
                      <div key={category.id}
                           className={`tabs-tab-content-item ${activeTab === category.id ? 'active' : ''}`}
                           data-content={`tab-${category.id}`}
                      >
                        <div className="article-list-wrapper">
                          {isLoading && <Spinner />}
                          {postsByCategory[category.id]?.map((post, index) => renderPost(post, index))}
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePaginationClick}
                            />
                        )}
                      </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
    );
  },

  save: () => {
    return null; // Rendered in PHP
  },
});
